import type { FC, ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'

import {
    Crown,
    Disc3,
    Feather,
    Gem,
    Hammer,
    MoveHorizontal
} from 'lucide-react'

import SectionHeading from '@/components/landing/section-heading'
import FitmentLab from '@/components/landing/fitment-lab'

interface Spec {
    label: string
    value: string
    copy: string
    icon: LucideIcon
    image: string
    imageAlt: string
}

const SPECS: Spec[] = [
    {
        label: 'Material',
        value: '6061-T6',
        copy: 'Forged from one solid billet of aerospace-grade aluminum, then heat treated to T6 for a dense, tough grain.',
        icon: Hammer,
        image: '/images/spec-material.jpg',
        imageAlt: 'Raw forged aluminum wheel blank beside a solid aerospace aluminum billet'
    },
    {
        label: 'Custom offset',
        value: 'ET -15 to +45',
        copy: 'Every set is specified to your hub, brakes and fender line, from flush and tight to aggressive and deep.',
        icon: MoveHorizontal,
        image: '/images/spec-offset.jpg',
        imageAlt: 'Side view of a deep dish forged wheel showing its stepped lip and recessed face'
    },
    {
        label: 'Weight',
        value: '8.4 kg',
        copy: 'Our signature specification is ultra light, so the car carries less unsprung mass into every corner.',
        icon: Feather,
        image: '/images/spec-weight.jpg',
        imageAlt: 'Ultra light forged wheel floating above a dark reflective floor'
    },
    {
        label: 'Spokes',
        value: '10-spoke',
        copy: 'Each spoke face is diamond cut to a bright, crisp edge that traces the light as the wheel turns.',
        icon: Gem,
        image: '/images/spec-spokes.jpg',
        imageAlt: 'Macro view of a diamond-cut spoke edge catching a gold highlight'
    },
    {
        label: 'Profile',
        value: 'Deep concave',
        copy: 'The spokes sweep back toward the hub, giving a depth you can read from across the street.',
        icon: Disc3,
        image: '/images/spec-profile.jpg',
        imageAlt: 'Low angle across a deep concave wheel face with spokes sweeping into the hub'
    },
    {
        label: 'Center cap',
        value: 'Floating titanium',
        copy: 'Engraved ALIF LAMATO SIGNATURE, the cap stays level while the wheel rotates beneath it.',
        icon: Crown,
        image: '/images/spec-cap.jpg',
        imageAlt: 'Macro view of a brushed titanium floating center cap with gold lug bolts'
    }
]

const MonoblockSection: FC = (): ReactNode => {
    return (
        <section id='monoblock' className='relative overflow-hidden bg-obsidian py-24 sm:py-32'>
            <div className='pointer-events-none absolute -left-40 top-1/3 size-[520px] rounded-full bg-gold/5 blur-3xl' aria-hidden='true' />

            <div className='relative mx-auto max-w-7xl px-5 sm:px-8'>
                <SectionHeading
                    eyebrow='The monoblock'
                    title={(
                        <>
                            Forged from a single billet.
                            <br className='hidden sm:block' />
                            {' '}
                            Engineered around your car.
                        </>
                    )}
                    description='No casting, no compromise. Each wheel begins as one piece of aerospace aluminum and is forged, machined and finished for a single car and a single owner.'
                />

                <figure className='relative mt-14 overflow-hidden rounded-xl border border-white/5 bg-slate-deep'>
                    <img
                        src='/images/monoblock-hero.jpg'
                        alt='Deep concave 10-spoke forged monoblock wheel in brushed titanium with a polished lip, lit by a gold rim light'
                        width={2100}
                        height={900}
                        loading='lazy'
                        decoding='async'
                        className='aspect-[16/9] w-full object-cover sm:aspect-[21/9]'
                    />
                    <div className='pointer-events-none absolute inset-0 bg-linear-to-r from-obsidian/80 via-obsidian/10 to-transparent' aria-hidden='true' />
                    <figcaption className='absolute bottom-6 left-6 max-w-xs sm:bottom-10 sm:left-10'>
                        <span className='font-mono text-[11px] uppercase tracking-[0.3em] text-gold'>
                            Signature monoblock
                        </span>
                        <span className='mt-3 block font-heading text-base font-bold leading-snug text-white sm:text-xl'>
                            10-spoke, deep concave, mirror lip.
                        </span>
                    </figcaption>
                </figure>

                <ul className='mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3'>
                    {SPECS.map((spec) => {
                        const Icon = spec.icon

                        return (
                            <li key={spec.label} className='group flex flex-col overflow-hidden rounded-xl border border-white/5 bg-slate-deep/60 transition-colors duration-300 hover:border-gold/30'>
                                <div className='relative aspect-[3/2] overflow-hidden bg-obsidian'>
                                    <img
                                        src={spec.image}
                                        alt={spec.imageAlt}
                                        width={1200}
                                        height={800}
                                        loading='lazy'
                                        decoding='async'
                                        className='size-full object-cover transition-transform duration-[1600ms] ease-out group-hover:scale-105 motion-reduce:transition-none'
                                    />
                                    <div className='pointer-events-none absolute inset-0 bg-linear-to-t from-obsidian/90 via-transparent to-transparent' aria-hidden='true' />
                                    <span className='absolute right-4 top-4 flex size-9 items-center justify-center rounded-full border border-white/10 bg-obsidian/70 backdrop-blur-sm'>
                                        <Icon className='size-4 text-gold' aria-hidden='true' />
                                    </span>
                                </div>

                                <div className='flex flex-1 flex-col p-7 sm:p-8'>
                                    <p className='font-mono text-[11px] uppercase tracking-[0.26em] text-gold'>
                                        {spec.label}
                                    </p>
                                    <h3 className='mt-4 font-heading text-2xl font-bold tracking-[0.02em] text-white'>
                                        {spec.value}
                                    </h3>
                                    <p className='mt-4 text-sm leading-relaxed text-titanium-light'>
                                        {spec.copy}
                                    </p>
                                </div>
                            </li>
                        )
                    })}
                </ul>

                <FitmentLab />
            </div>
        </section>
    )
}

export default MonoblockSection
